import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator } from "../components/ui";
import { ContextBar } from "../components/context-bar";
import { KeyVal, StateTag, formatAge, shortHash } from "../components/phase3";
import {
  ApiVsVerifiedNote,
  ProgressTimeline,
  SafeError,
  deploymentTone,
  verificationLabel,
  type TimelineStep,
} from "../components/phase4";
import { EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  type DeploymentReportView,
  type DeploymentStatus,
  type DeploymentView,
} from "../lib/api";

// Statuses ODIN is actively working; while in one of these we poll the server.
const ACTIVE_STATUSES: ReadonlySet<DeploymentStatus> = new Set<DeploymentStatus>([
  "QUEUED",
  "PREPARING",
  "APPLYING",
  "VERIFYING",
  "ROLLBACK_QUEUED",
  "ROLLING_BACK",
]);

const RANK: Record<DeploymentStatus, number> = {
  DRAFT: 0,
  PLANNED: 0,
  BLOCKED: 0,
  AWAITING_APPROVAL: 1,
  APPROVED: 2,
  QUEUED: 3,
  PREPARING: 4,
  APPLYING: 5,
  VERIFYING: 6,
  VERIFIED: 7,
  PARTIALLY_APPLIED: 6,
  FAILED: 5,
  ROLLBACK_AVAILABLE: 5,
  ROLLBACK_QUEUED: 5,
  ROLLING_BACK: 5,
  ROLLED_BACK: 7,
  ROLLBACK_FAILED: 5,
  CANCELLED: 0,
  STALE: 0,
  SUPERSEDED: 0,
};

const FAILURE_STATUSES: ReadonlySet<DeploymentStatus> = new Set<DeploymentStatus>([
  "FAILED",
  "PARTIALLY_APPLIED",
  "ROLLBACK_FAILED",
]);

function buildSteps(status: DeploymentStatus, report: DeploymentReportView | null): TimelineStep[] {
  const rank = RANK[status];
  const failed = FAILURE_STATUSES.has(status);
  const stepState = (atRank: number): TimelineStep["state"] => {
    if (rank > atRank) return "done";
    if (rank === atRank) return failed ? "failed" : "active";
    return "pending";
  };
  const attempts = report?.content.operationResults ?? [];
  const retried = attempts.filter((o) => o.attempts > 1);
  const completed = attempts.filter((o) => o.state === "SUCCEEDED" || o.state === "VERIFIED");

  return [
    {
      key: "queued",
      label: "Approved & queued",
      state: rank >= 3 ? "done" : rank === 2 ? "active" : "pending",
      detail: <span className="muted">Handed to the worker, which performs the authoritative live recheck.</span>,
    },
    {
      key: "preparing",
      label: "Preparing — fresh snapshot & revalidation",
      state: stepState(4),
      detail: (
        <span className="muted">
          {report?.content.preSnapshotHash ? `Pre-deploy snapshot ${shortHash(report.content.preSnapshotHash)}` : "A fresh snapshot is captured and the approval is revalidated before any change."}
        </span>
      ),
    },
    {
      key: "applying",
      label: "Applying operations",
      state: stepState(5),
      detail:
        attempts.length > 0 ? (
          <span className="muted">
            {completed.length}/{attempts.length} operations completed{retried.length > 0 ? ` · ${retried.length} needed a retry` : ""}
          </span>
        ) : (
          <span className="muted">Per-operation progress appears in the report once the run completes.</span>
        ),
    },
    {
      key: "verifying",
      label: "Verifying resulting state",
      state: stepState(6),
      detail: report?.content.verification ? (
        <span className="muted">{verificationLabel(report.content.verification.result)}</span>
      ) : (
        <span className="muted">ODIN re-reads the live server and compares it to the approved plan.</span>
      ),
    },
    {
      key: "final",
      label: "Final status",
      state: status === "VERIFIED" || status === "ROLLED_BACK" ? "done" : failed ? "failed" : rank >= 7 ? "done" : "pending",
      detail: <StatusIndicator tone={deploymentTone(status)} label={status} />,
    },
  ];
}

// ── Presentational (testable) ────────────────────────────────────────────────
export function DeploymentProgressView({
  deployment,
  report,
  polling = false,
  now = Date.now(),
}: {
  deployment: DeploymentView;
  report: DeploymentReportView | null;
  polling?: boolean;
  now?: number;
}): JSX.Element {
  const status = deployment.status;
  const content = report?.content ?? null;
  const failures = content?.failures ?? [];
  const retried = (content?.operationResults ?? []).filter((o) => o.attempts > 1);

  return (
    <div className="deployment-progress">
      <Card title="Live status">
        <div className="kv-grid">
          <KeyVal k="Status" v={<StatusIndicator tone={deploymentTone(status)} label={status} />} />
          <KeyVal k="Plan hash" v={shortHash(deployment.planHash)} mono />
          <KeyVal k="Last updated" v={formatAge(deployment.updatedAt, now)} />
          <KeyVal k="Verification" v={verificationLabel(deployment.verificationOutcome)} />
        </div>
        {polling ? (
          <p className="section-note" aria-live="polite">
            ODIN is working. This page polls the server, so you can safely leave and come back — the status is never held
            only in your browser.
          </p>
        ) : (
          <p className="section-note">This deployment is not currently in flight.</p>
        )}
      </Card>

      <Card title="Progress">
        <ProgressTimeline steps={buildSteps(status, report)} />
      </Card>

      {retried.length > 0 ? (
        <Card title={`Retries (${retried.length})`}>
          <div className="table">
            <div className="table__head">
              <span>Resource</span>
              <span>Operation</span>
              <span>Attempts</span>
            </div>
            {retried.map((o, i) => (
              <div className="table__row" key={`${o.resourceKey}-${i}`}>
                <span>{o.resourceKey}</span>
                <span className="mono">{o.operationType}</span>
                <span className="mono">{o.attempts}</span>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {failures.length > 0 ? (
        <Card title={`Errors (${failures.length})`}>
          {/* Safe, human-readable errors only — never tokens, headers, stack traces or raw Discord objects. */}
          <div className="finding-list">
            {failures.map((f, i) => (
              <div className="finding-row" key={`${f.resourceKey}-${i}`}>
                <div className="op-row__head">
                  <span className="marker marker--destructive">{f.errorCode}</span>
                  <code className="op-row__key">{f.resourceKey}</code>
                  <span className="op-row__action">{f.operationType}</span>
                </div>
                <p className="finding-row__plain">{f.errorSummary}</p>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {content ? (
        <Card title="Verification">
          <ApiVsVerifiedNote />
          <div className="kv-grid">
            <KeyVal k="Result" v={verificationLabel(content.verification?.result)} />
            <KeyVal k="Completed operations" v={(content.operationResults ?? []).filter((o) => o.state === "SUCCEEDED" || o.state === "VERIFIED").length} />
            <KeyVal k="Skipped" v={content.skipped?.length ?? 0} />
            <KeyVal k="Post-deploy snapshot" v={shortHash(content.postSnapshotHash)} mono />
          </div>
          {content.verification && content.verification.warnings.length > 0 ? (
            <ul className="section-note">
              {content.verification.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          ) : null}
          <p className="section-note">
            When ODIN finishes, a full <StateTag state="current" /> vs approved comparison is available on the{" "}
            <Link to="/app/release-report">release report</Link>.
          </p>
        </Card>
      ) : (
        <Card>
          <p className="muted">
            No report yet. The immutable release report — completed operations, verification and any errors — is produced
            when the run finishes.
          </p>
        </Card>
      )}
    </div>
  );
}

// ── Data-fetching container ──────────────────────────────────────────────────
export function DeploymentProgress(): JSX.Element {
  const ctx = useAppContext();
  const [deploymentId, setDeploymentId] = useState("");
  const [tracking, setTracking] = useState<string | null>(null);
  const [deployment, setDeployment] = useState<DeploymentView | null>(null);
  const [report, setReport] = useState<DeploymentReportView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const polling = deployment ? ACTIVE_STATUSES.has(deployment.status) : false;

  // Fetch the deployment + (if produced) its report. Returns the status so the
  // poller can decide whether to keep going.
  const fetchOnce = async (tenantId: string, id: string): Promise<DeploymentStatus | null> => {
    try {
      const dep = await api.getDeployment(tenantId, id);
      setDeployment(dep);
      // The report exists only once the worker has produced it; a 404 is expected before then.
      try {
        setReport(await api.getDeploymentReport(tenantId, id));
      } catch {
        setReport(null);
      }
      return dep.status;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load the deployment");
      return null;
    }
  };

  // Poll while the deployment is actively being worked on; stop once it settles.
  useEffect(() => {
    const tenantId = ctx.tenantId;
    if (!tracking || !tenantId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const loop = async () => {
      if (cancelled) return;
      const status = await fetchOnce(tenantId, tracking);
      if (cancelled) return;
      if (status && ACTIVE_STATUSES.has(status)) {
        timer = setTimeout(() => void loop(), 4000);
      }
    };
    void loop();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [tracking, ctx.tenantId]);

  const track = async () => {
    if (!ctx.tenantId || deploymentId.trim().length < 1) return;
    setError(null);
    setLoading(true);
    setReport(null);
    setDeployment(null);
    const id = deploymentId.trim();
    setTracking(id);
    await fetchOnce(ctx.tenantId, id);
    setLoading(false);
  };

  return (
    <>
      <PageHeader title="Deployment progress">
        <p>
          Track a queued release as ODIN prepares, applies and verifies it. Status is polled from the server — leaving
          and returning never loses progress. Approve or queue a release on the <Link to="/app/release-review">release review</Link> page.
        </p>
      </PageHeader>

      {!apiConfigured ? (
        <NotConfiguredState title="Control API URL not configured">
          Set <code>VITE_CONTROL_API_URL</code> to track deployments.
        </NotConfiguredState>
      ) : (
        <>
          <ContextBar />
          {!ctx.tenantId ? (
            <EmptyState title="Select a workspace">Choose a workspace above, then track a deployment by id.</EmptyState>
          ) : (
            <>
              <Card title="Track a deployment" description="Enter a deployment id to follow its progress.">
                <div className="row">
                  <input
                    className="field__input"
                    placeholder="Deployment id"
                    value={deploymentId}
                    onChange={(e) => setDeploymentId(e.target.value)}
                    aria-label="Deployment id"
                  />
                  <Button variant="primary" disabled={loading || deploymentId.trim().length < 1} onClick={track}>
                    {loading ? "Loading…" : "Track"}
                  </Button>
                </div>
              </Card>

              {error ? <SafeError message={error} /> : null}
              {loading && !deployment ? <LoadingState label="Loading status…" /> : null}
              {deployment ? <DeploymentProgressView deployment={deployment} report={report} polling={polling} /> : null}
            </>
          )}
        </>
      )}
    </>
  );
}
